import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CreditCard, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function SubscriptionManagement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/current');
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const response = await api.post('/subscriptions/cancel', {
        reason: 'User requested cancellation'
      });
      toast.success(t('subscription.cancelSuccess'));
      setSubscription({
        ...subscription,
        cancel_at_period_end: true
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('subscription.cancelFailed'));
    } finally {
      setCanceling(false);
    }
  };

  const handleChangePlan = () => {
    navigate('/pricing');
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500">{t('common.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  const isFreePlan = !subscription || user?.plan === 'free';
  const planName = user?.plan?.replace('_', ' ') || 'free';
  const planTier = planName.includes('basic') ? 'basic' : planName.includes('pro') ? 'pro' : 'free';
  const billingInterval = planName.includes('monthly') ? t('pricing.monthly') : planName.includes('yearly') ? t('pricing.yearly') : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          {t('subscription.title')}
        </CardTitle>
        <CardDescription>{t('subscription.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Plan */}
        <div className="border rounded-lg p-4 bg-gradient-to-r from-emerald-50 to-blue-50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold capitalize">{planTier} {billingInterval && `(${billingInterval})`}</h3>
            <span className="px-3 py-1 bg-emerald-600 text-white text-sm font-semibold rounded-full">
              {isFreePlan ? t('subscription.free') : t('subscription.active')}
            </span>
          </div>
          
          {!isFreePlan && subscription && (
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {t('subscription.renewsOn')}: {new Date(subscription.current_period_end).toLocaleDateString()}
                </span>
              </div>
              
              {subscription.cancel_at_period_end && (
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertCircle className="h-4 w-4" />
                  <span>{t('subscription.cancelsAt')}: {new Date(subscription.current_period_end).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleChangePlan}
            variant="default"
            className="flex-1"
          >
            {isFreePlan ? t('subscription.upgrade') : t('subscription.changePlan')}
          </Button>

          {!isFreePlan && subscription && !subscription.cancel_at_period_end && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="flex-1">
                  {t('subscription.cancel')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('subscription.confirmCancel')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('subscription.cancelDescription')}
                    <br /><br />
                    <strong>{t('subscription.accessUntil')}: {new Date(subscription.current_period_end).toLocaleDateString()}</strong>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancelSubscription}
                    disabled={canceling}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {canceling ? t('common.loading') : t('subscription.confirmCancelButton')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Plan Features Info */}
        <div className="text-sm text-gray-500 border-t pt-4">
          <p>{t('subscription.planInfo')}</p>
        </div>
      </CardContent>
    </Card>
  );
}
